import { useRef, useState, useEffect, forwardRef } from "react";
import { ToastContainer } from "react-toastify";
import MaterialTable from "material-table";
import { Paper } from "@material-ui/core";
import _ from "lodash";
import axios from "axios";
import jsPDF from "jspdf";
import { autoTable } from 'jspdf-autotable'
import { useNavigate } from "react-router-dom";
import { getTrans as t } from "../../Localization/translations";
import authHeader from "../../services/auth-header";
import { API } from "../../backend";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";


function SubClassEditor(props) {
  const classId = props.rowData?.account_class_id || "";

  const [subClasses, setSubClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classId) {
      setSubClasses([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    axios
      .get(`${API}accounting/account-subclasses/?class_id=${classId}`, {
        headers: authHeader(),
      })
      .then(res => {
        const subs = res.data.data || res.data || [];
        setSubClasses(subs);
      })
      .catch(() => {
        setSubClasses([]);
      })
      .finally(() => setLoading(false));
  }, [classId]);

  return (
    <select
      className="form-control"
      value={props.value || ""}
      onChange={(e) => {
        const value = e.target.value || null;
        props.onChange(value);
      }}
      disabled={!classId || loading}
    >
      <option value="">Select SubClass</option>

      {loading ? (
        <option disabled>Loading subclasses...</option>
      ) : (
        subClasses.map(s => (
          <option key={s.id} value={s.id}>
            {s.subclass_name}
          </option>
        ))
      )}
    </select>
  );
}

function Accounts() {

  const [data, setData] = useState([]);
  const tableRef = useRef();
  const ENABLED = {
    Y: "Yes",
    N: "No",
  };
  const MANUAL = {
    Y: "Yes",
    N: "No",
  };
  const [permissionss, setPermission] = useState(localStorage.getItem('permissions') || '');
  const permissionsArray = permissionss ? permissionss.split(',') : [];

  const [accountClasses, setAccountClasses] = useState([]);

  useEffect(() => {
    axios
      .get(API + "accounting/account-classes/", { headers: authHeader() })
      .then(res => setAccountClasses(res.data || []))
      .catch(() => setAccountClasses([]));
  }, []);

  const fetchAllDataForExport = async () => {
    let allData = [];
    let currentPage = 0;
    const pageSize = 10;

    while (true) {
      let url = API + "accounting/accounts/?";
      url += "per_page=" + pageSize;
      url += "&page=" + (currentPage + 1);

      try {
        const response = await fetch(url, { headers: authHeader() });
        const result = await response.json();

        if (result.data.length === 0) {
          break;
        }

        allData = allData.concat(result.data);
        currentPage++;
      } catch (error) {
        console.error("Error fetching data:", error);
        break;
      }
    }

    return allData;
  };

  const tableIcons = {
    Edit: forwardRef((props, ref) => (
      <img
        ref={ref}
        {...props}
        src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"}
        alt="edit"
        style={{ cursor: "pointer", width: 18 }}
      />
    )),

    Delete: forwardRef((props, ref) => (
      <img
        ref={ref}
        {...props}
        src={process.env.PUBLIC_URL + "/static/images/ic-delete.svg"}
        alt="delete"
        style={{ cursor: "pointer", width: 18 }}
      />
    )),

    DeleteOutline: forwardRef((props, ref) => (
      <img
        ref={ref}
        {...props}
        src={process.env.PUBLIC_URL + "/static/images/ic-delete.svg"}
        alt="delete"
        style={{ cursor: "pointer", width: 18 }}
      />
    )),
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: "",
      message: "Are you sure you want to delete this account?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {

            axios
              .delete(API + `accounting/account-delete/${id}/`, {
                headers: authHeader(),
              })
              .then(() => {

                toast.success("Account deleted successfully", {
                  position: "top-right",
                  autoClose: 1000,
                });

                tableRef.current && tableRef.current.onQueryChange();
              })
              .catch(() => {
                toast.error("Failed to delete account", {
                  position: "top-right",
                  autoClose: 1000,
                });
              });

          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  const canChangeAccounts =
    permissionss && permissionsArray.includes("change_accounts");

  const canEditControllerFields =
    permissionss && permissionsArray.includes("can_view_controller");

  const navigate = useNavigate();

  const handleAdd = () => {
    navigate("/addaccounts");
  };

  const handleEdit = (id) => {
    navigate("/editaccounts", { state: id });
  };

  return (
    <div className="container-fluid py-4 pt-2">
      <div className="row py-2 mt-2">
        <ToastContainer />
        <div className="col-md-12">
          <div className="mb-0">
            <div className="header pb-0">
              <div className="d-lg-flex">
                <div></div>
              </div>
            </div>
            <div className="body">
              <div className="p-0">
                <MaterialTable
                  tableRef={tableRef}
                  icons={tableIcons}
                  title="Accounts"
                  style={{
                    padding: "0.5rem 0.5rem",
                    backgroundColor: "var(--bs-table-bg)",
                    borderBottomWidth: "1px",
                    boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                  }}
                  columns={[
                    {
                      title: `${t("Account Name")}`,
                      field: "account_code",
                      editable: canEditControllerFields ? "always" : "never",
                      render: (rowData) => (
                        <>
                          <div className="d-xl-none tc d-md-none d-lg-none">
                            {t("Account Name")}
                          </div>
                          <div>{rowData.account_code}</div>
                        </>
                      ),
                      headerStyle: {
                        verticalAlign: "inherit",
                        fontWeight: "bold",
                        textAlign: "left",
                      },
                      cellStyle: {
                        textAlign: "left",
                        fontSize: 12,
                      },
                    },
                    {
                      title: `${t("Account Number")}`,
                      field: "account_number",
                      editable: canEditControllerFields ? "always" : "never",
                      render: (rowData) => (
                        <>
                          <div className="d-xl-none tc d-md-none d-lg-none">
                            {t("Account Number")}
                          </div>
                          <div>{rowData.account_number}</div>
                        </>
                      ),
                      headerStyle: {
                        verticalAlign: "inherit",
                        fontWeight: "bold",
                        textAlign: "left",
                      },
                      cellStyle: {
                        textAlign: "left",
                        fontSize: 12,
                      },
                    },
                    {
                      title: "Account Class",
                      field: "account_class_id",
                      render: rowData => rowData.account_class?.class_name || "—",
                      editComponent: props => (
                        <select
                          className="form-control"
                          value={props.value || ""}
                          onChange={e => {
                            const value = e.target.value;
                            props.onChange(value);

                            if (props.rowData) {
                              props.onRowDataChange({
                                ...props.rowData,
                                account_class_id: value,
                                account_subclass_id: null
                              });
                            }
                          }}
                        >
                          <option value="">Select Class</option>
                          {accountClasses.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.class_name}
                            </option>
                          ))}
                        </select>
                      ),
                      headerStyle: {
                        verticalAlign: "inherit",
                        fontWeight: "bold",
                        textAlign: "left",
                      },
                      cellStyle: {
                        textAlign: "left",
                        fontSize: 12,
                      },
                    },
                    {
                      title: "Account SubClass",
                      field: "account_subclass_id",
                      render: rowData => {
                        if (!rowData.account_subclass_id) {
                          return "—";
                        }
                        return rowData.account_subclass?.subclass_name || "—";
                      },
                      editComponent: SubClassEditor,
                      headerStyle: {
                        verticalAlign: "inherit",
                        fontWeight: "bold",
                        textAlign: "left",
                      },
                      cellStyle: {
                        textAlign: "left",
                        fontSize: 12,
                      },
                    },
                    {
                      title: "Manual",
                      field: "manual",
                      lookup: MANUAL,
                      render: (rowData) => (
                        <>
                          <div className="d-xl-none tc d-md-none d-lg-none">Manual</div>
                          <div>{MANUAL[rowData.manual]}</div>
                        </>
                      ),
                      headerStyle: {
                        verticalAlign: "inherit",
                        fontWeight: "bold",
                        textAlign: "left",
                      },
                      cellStyle: {
                        textAlign: "left",
                        fontSize: 12,
                      },
                    },

                    {
                      title: `${t("Enabled")}`,
                      field: "active",
                      lookup: ENABLED,
                      render: (rowData) => (
                        <>
                          <div className="d-xl-none tc d-md-none d-lg-none">{t("Enabled")}</div>
                          <div>{ENABLED[rowData.active]}</div>
                        </>
                      ),
                      headerStyle: {
                        verticalAlign: "inherit",
                        fontWeight: "bold",
                        textAlign: "left",
                      },
                      cellStyle: {
                        textAlign: "left",
                        fontSize: 12,
                      },
                    },
                  ]}

                  actions={[
                    {
                      icon: () => (
                        <img
                          src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"}
                          alt="edit"
                          style={{ cursor: "pointer", width: 18 }}
                        />
                      ),
                      tooltip: "Edit Account",
                      onClick: (event, rowData) => {
                        if (!tableRef.current) return;
                        tableRef.current.dataManager.changeRowEditing(rowData, "update");
                        tableRef.current.setState({
                          ...tableRef.current.dataManager.getRenderState(),
                          showAddRow: false
                        });
                      }
                    },
                    ...(permissionss && permissionsArray.includes('can_view_controller')
                      ? [{

                        icon: () => (
                          <img
                            src={process.env.PUBLIC_URL + "/static/images/ic-delete.svg"}
                            alt="delete"
                            style={{ cursor: "pointer", width: 18 }}
                          />
                        ),
                        tooltip: "Delete Account",
                        onClick: (event, rowData) => {
                          handleDelete(rowData.id);
                        }
                      }]
                      : [])
                  ]}

                  data={(query) =>
                    new Promise((resolve, reject) => {
                      const { orderBy, orderDirection } = query;

                      let url = API + "accounting/accounts/?";
                      url += "per_page=" + query.pageSize;
                      url += "&page=" + (query.page + 1);
                      if (orderBy) {
                        url += `&orderBy=${orderBy.field}`;
                        url += `&orderDirection=${orderDirection}`;
                      }

                      fetch(url, { headers: authHeader() })
                        .then((response) => response.json())
                        .then((result) => {
                          const formatted = (result.data || []).map(row => ({
                            ...row,
                            account_class_id: row.account_class?.id ?? null,
                            account_subclass_id: row.account_subclass?.id ?? null,
                          }));

                          resolve({
                            data: formatted,
                            page: result.page - 1,
                            totalCount: result.total,
                          });
                        })
                        .catch(reject);
                    })
                  }
                  editable={
                    permissionss && permissionsArray.includes("change_accounts")
                      ? {
                        isEditHidden: () => true,
                        onRowAdd: (newData) =>
                          new Promise((resolve, reject) => {

                            const payload = {
                              account_code: newData.account_code,
                              account_number: newData.account_number,
                              account_class: newData.account_class_id || null,
                              account_subclass: newData.account_subclass_id || null,
                              manual: newData.manual || "N",
                              active: newData.active || "Y",
                            };

                            axios.post(
                              API + "accounting/account-create/",
                              payload,
                              { headers: authHeader() }
                            )
                              .then(() => {
                                resolve();
                                tableRef.current && tableRef.current.onQueryChange();
                              })
                              .catch(() => reject());
                          }),

                        onRowUpdate: (newData, oldData) =>
                          new Promise((resolve, reject) => {

                            let subclassValue = newData.account_subclass_id;

                            // If account class changed AND subclass was not changed by user
                            if (newData.account_class_id !== oldData.account_class_id) {

                              // If subclass still equals previous subclass → user didn't select new one
                              if (newData.account_subclass_id === oldData.account_subclass_id) {
                                subclassValue = null;
                              }
                            }

                            const payload = {
                              account_code: newData.account_code,
                              account_number: newData.account_number,
                              account_class: newData.account_class_id || null,
                              account_subclass: subclassValue,
                              manual: newData.manual,
                              active: newData.active,
                            };

                            axios.post(
                              API + `accounting/account-edit/${oldData.id}/`,
                              payload,
                              { headers: authHeader() }
                            )
                              .then(() => {
                                resolve();
                                tableRef.current && tableRef.current.onQueryChange();
                              })
                              .catch(() => reject());

                          })


                      }
                      : undefined
                  }
                  components={{
                    Container: (props) => (
                      <div id="Payment">
                        <Paper {...props} />
                      </div>
                    ),

                    Toolbar: (props) => (
                      <div className="d-flex justify-content-between align-items-center p-2">
                        <div>{props.title}</div>

                        {
                          permissionss &&
                          permissionsArray.includes("can_view_controller") && (
                            <button
                              className="btn btn-primary"
                              onClick={handleAdd}
                            >
                              + Add Account
                            </button>
                          )
                        }
                      </div>
                    ),
                  }}
                  options={{
                    headerStyle: {
                      backgroundColor: "white",
                      color: "#245C7F",
                      fontSize: 12,
                    },
                    rowStyle: (rowData, index) => {
                      if (index % 2) {
                        return { backgroundColor: "#fff" };
                      }
                    },
                    actionsColumnIndex: -1,
                    showEditIcon: false,
                    showTextRowsSelected: false,
                    sorting: true,
                    search: false,
                    toolbar: true,
                    paging: true,
                    exportAllData: true,
                    exportButton: true,
                    exportFileName: "Accounts",
                    pageSize: 10,
                    emptyRowsWhenPaging: false,
                    pageSizeOptions: [10, 15, 20, 50],
                    paginationType: "stepped",
                    showFirstLastPageButtons: false,
                    confirmDeletion: false,
                    actionsColumnIndex: -1,
                    showEditIcon: false,
                    showTextRowsSelected: false,
                    addRowPosition: "first",
                    exportCsv: async (columns, data) => {
                      try {
                        const allData = await fetchAllDataForExport();
                        const title = "Accounts Report";
                        const currentDate = new Date().toLocaleDateString();
                        const csvContentWithInfo = `${title}\n Date: ${currentDate}\n \n\n`;
                        const csvColumns = columns.map(columnDef => columnDef.title);

                        const csvData = allData.map(rowData => {
                          return columns.map(columnDef => rowData[columnDef.field] || '');
                        });

                        const csvRows = [csvColumns, ...csvData];
                        const csvContent = `${csvContentWithInfo}${csvRows.map(row => row.join(',')).join('\n')}`;

                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = 'Accounts.csv';
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      } catch (error) {
                        console.error("Error exporting CSV:", error);
                      }
                    },
                    exportPdf: async (columns, data) => {
                      const allData = await fetchAllDataForExport();

                      const doc = new jsPDF();
                      doc.setFont("helvetica", "bold");
                      doc.setFontSize(16);

                      const title = "Accounts Report";
                      doc.text(title, 14, 10);

                      doc.setFontSize(12);
                      doc.setFont("helvetica", "normal");
                      const currentDate = new Date().toLocaleDateString();
                      doc.text(`Date: ${currentDate}`, 14, 15);

                      const tableStartY = 27;
                      const columnTitles = columns.map(columnDef => columnDef.title);
                      const pdfData = allData.map(rowData =>
                        columns.map(columnDef => rowData[columnDef.field] ?? '')
                      );
                      const tableData = [columnTitles, ...pdfData];

                      autoTable(doc, {
                        head: [tableData[0]],
                        body: tableData.slice(1),
                        startY: tableStartY,
                      });

                      doc.save(`Accounts.pdf`);
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Accounts;